import type { FC } from 'react'
import type { Locale } from '@/i18n/config'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { Check, ChevronDown, Globe } from 'lucide-react'

import { cn } from '@/lib/utils'
import { localeMeta, locales } from '@/i18n/config'
import { useLocale, useT } from '@/i18n'

interface LanguageSwitcherProps {
    /** `menu` is the compact dropdown in the header, `list` the row of languages in the mobile menu. */
    variant?: 'menu' | 'list'
    /** Header colours: light text on the dark hero, dark text once the header turns solid. */
    scrolled?: boolean
    onNavigate?: () => void
}

export const LanguageSwitcher: FC<LanguageSwitcherProps> = ({ variant = 'menu', scrolled = false, onNavigate }) => {
    const router = useRouter()
    const locale = useLocale()
    const t = useT()
    const [open, setOpen] = useState(false)
    const rootRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!open) return
        const onPointer = (event: PointerEvent) => {
            if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
        }
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false)
        }
        document.addEventListener('pointerdown', onPointer)
        document.addEventListener('keydown', onKey)
        return () => {
            document.removeEventListener('pointerdown', onPointer)
            document.removeEventListener('keydown', onKey)
        }
    }, [open])

    useEffect(() => {
        const close = () => setOpen(false)
        router.events.on('routeChangeStart', close)
        return () => router.events.off('routeChangeStart', close)
    }, [router.events])

    const codes = locales as Locale[]

    if (variant === 'list') {
        return (
            <nav aria-label={t.common.chooseLanguage} className='flex flex-wrap gap-2'>
                {codes.map((code) => (
                    <Link
                        key={code}
                        href={router.asPath}
                        locale={code}
                        lang={code}
                        dir={localeMeta[code].dir}
                        hrefLang={code}
                        scroll={false}
                        onClick={onNavigate}
                        aria-current={code === locale ? 'true' : undefined}
                        className={cn(
                            'rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors',
                            code === locale
                                ? 'border-gold bg-gold/15 text-gold-soft'
                                : 'border-parchment/20 text-parchment/80 hover:border-parchment/50 hover:text-parchment'
                        )}
                    >
                        {localeMeta[code].nativeName}
                    </Link>
                ))}
            </nav>
        )
    }

    return (
        <div ref={rootRef} className='relative'>
            <button
                type='button'
                onClick={() => setOpen((value) => !value)}
                aria-expanded={open}
                aria-haspopup='true'
                aria-label={`${t.common.chooseLanguage}: ${localeMeta[locale].nativeName}`}
                className={cn(
                    'inline-flex h-9 items-center gap-1.5 rounded-full border px-3 text-sm font-semibold uppercase transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/70',
                    scrolled
                        ? 'border-border/80 text-foreground/80 hover:border-navy/40 hover:text-foreground'
                        : 'border-parchment/30 text-parchment/85 hover:border-parchment hover:text-parchment'
                )}
            >
                <Globe className='size-4' />
                {locale}
                <ChevronDown className={cn('size-3.5 transition-transform', open && 'rotate-180')} />
            </button>

            {open && (
                <div className='absolute end-0 top-full z-50 mt-2 w-48 overflow-hidden rounded-xl border border-border bg-card p-1.5 shadow-[0_24px_50px_-24px_rgba(20,25,45,0.55)]'>
                    <p className='px-3 pb-1.5 pt-1 text-xs font-medium text-muted-foreground'>{t.common.language}</p>
                    <ul>
                        {codes.map((code) => (
                            <li key={code}>
                                <Link
                                    href={router.asPath}
                                    locale={code}
                                    lang={code}
                                    dir={localeMeta[code].dir}
                                    hrefLang={code}
                                    scroll={false}
                                    aria-current={code === locale ? 'true' : undefined}
                                    className={cn(
                                        'flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
                                        code === locale
                                            ? 'bg-navy/[0.06] font-semibold text-navy'
                                            : 'text-foreground/80 hover:bg-muted hover:text-foreground'
                                    )}
                                >
                                    {localeMeta[code].nativeName}
                                    {code === locale && <Check className='size-4 text-gold-deep' />}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    )
}
